import { useMemo } from "react";

import { Report } from "@/types/evaluate";
import { generateColor, extractScoreFromReport } from "../utils";
import { useNavigate, useParams } from "react-router-dom";

import { TimestampData, ChartDataset } from "../types";

const useChartDatasets = (
  reportGroups: Record<string, Report[]>,
  timestampData: TimestampData,
): ChartDataset[] => {
  const navigate = useNavigate();

  const { promptId } = useParams();

  return useMemo(() => {
    if (
      Object.keys(reportGroups).length === 0 ||
      timestampData.uniqueDates.length === 0
    ) {
      return [];
    }

    const { uniqueDates, timestampIndexMap } = timestampData;
    const datasets: ChartDataset[] = [];

    Object.entries(reportGroups).forEach(([groupKey, groupReports]) => {
      const datasetGroups = groupReports.reduce<Record<string, Report[]>>(
        (acc, report) => {
          const datasetId = report.dataset_id;
          if (!acc[datasetId]) {
            acc[datasetId] = [];
          }
          acc[datasetId].push(report);
          return acc;
        },
        {},
      );

      Object.entries(datasetGroups).forEach(([datasetId, datasetReports]) => {
        const baseColor = generateColor(`${groupKey}`);
        const firstReport = datasetReports[0];

        const reportName = firstReport?.parent_report.name || "Unknown";
        const datasetGroupName =
          firstReport?.dataset?.dataset_group?.name || "Unknown";
        const versionSuffix = firstReport?.dataset?.version_number
          ? ` v${firstReport.dataset.version_number}`
          : "";
        const datasetName = datasetGroupName + versionSuffix;

        const dataPoints = Array(uniqueDates.length).fill(null);
        const pointMetadata = Array(uniqueDates.length).fill(null);

        datasetReports.forEach((report) => {
          const timestamp = new Date(report.created_at).getTime();
          const dataIndex = timestampIndexMap[timestamp];

          if (dataIndex !== undefined) {
            const score = extractScoreFromReport(report);
            dataPoints[dataIndex] = score;

            const associatedPromptVersions = report.columns
              .filter(
                (col) =>
                  col?.column_type === "PROMPT_TEMPLATE" &&
                  col?.configuration?.frozen_template_data?.prompt_id ===
                    Number(promptId!),
              )
              .map((col) => `#${col?.configuration?.template?.version_number}`)
              .join(",");

            pointMetadata[dataIndex] = {
              batchName: report.name,
              reportName: reportName,
              datasetName:
                (report?.dataset?.dataset_group?.name || "Unknown") +
                (report?.dataset?.version_number
                  ? ` v${report?.dataset?.version_number}`
                  : ""),
              timestamp: new Date(report.created_at).toLocaleString(),
              scoreValue: score ? score.toFixed(2) : "-",
              version: `${associatedPromptVersions || "-"}`,
              hasComment: Boolean(report.comment),
              comment: report.comment,
              parentName: report.parent_report_id || "-",
              reportId: report.id.toString(),
              navigate: () => {
                if (report.parent_report_id)
                  navigate(
                    `/workspace/${report.workspace_id}/evaluate/blueprints/${report.parent_report_id}/reports/${report.id}`,
                  );
              },
            };
          }
        });

        datasets.push({
          label: `${reportName} - ${datasetGroupName}`,
          reportName: reportName,
          datasetId: datasetId,
          datasetName: datasetName,
          data: dataPoints,
          borderColor: baseColor,
          backgroundColor: `${baseColor}20`,
          tension: 0.4,
          fill: false,
          pointRadius: 4,
          pointBackgroundColor: "white",
          pointBorderColor: baseColor,
          pointBorderWidth: 2,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: "white",
          pointHoverBorderColor: baseColor,
          pointHoverBorderWidth: 3,
          borderWidth: 2,
          pointMetadata,
          spanGaps: true,
        });
      });
    });

    return datasets;
  }, [reportGroups, timestampData, promptId, navigate]);
};

export default useChartDatasets;
